import { useQuery, useQueryClient, type QueryKey, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import type { Table } from 'dexie';
import { useAuth } from '../auth/AuthProvider';
import {
  getCampaign,
  getCampaigns,
  getCharacter,
  getCharacters,
  getTableRound,
  getTableRounds,
  getWorldElement,
  getWorldElements,
  getWorldRelationshipsForSource,
  getWorldRelationshipsToTarget,
  getWorldTree,
  getWorldTrees,
  type RelationshipEndpointType,
  type WorldTreeFull,
} from '../api/endpoints';
import { queryKeys } from '../api/queryKeys';
import type {
  Campaign,
  Character,
  TableRound,
  WorldElement,
  WorldRelationship,
  WorldTree,
  WorldTreeNode,
} from '../api/types';
import { isExplicitNsfwSystemNarrator } from '../shared/narratorSort';
import {
  bulkGetCatalogElements,
  readCatalogElement,
  readCatalogElements,
  readCatalogRelationships,
} from './catalogDb';
import {
  chatCacheKey,
  getMetaValue,
  type CatalogVisibleRecord,
  type ChatCacheKind,
  type ChatCacheRecord,
  type LorathLocalDatabase,
} from './db';
import { useOnline } from './online';
import { useSyncDatabase } from './SyncProvider';

export interface WorldElementLocalFilter {
  type?: string;
  exclude_game_settings?: boolean;
  // kosmosId filtert clientseitig auf den aktiven Kosmos + die system-
  // übergreifenden Seeds (kosmos_id NULL). Wird von der WorldElements-Seite
  // gesetzt, damit der Kosmos-Wechsel OFFLINE ohne erneuten Server-Sync
  // funktioniert. NICHT als Server-Query genutzt — der Server-Bootstrap
  // zieht ohnehin alle Kosmen (siehe engine.ts fetchBootstrapData).
  kosmosId?: number | null;
}

export interface WorldRelationshipLocalFilter {
  sourceType?: RelationshipEndpointType;
  sourceId?: number | string;
  targetType?: RelationshipEndpointType;
  targetId?: number | string;
}

export interface ChatCacheHookResult {
  messages: unknown[];
  fetchedAt: string | null;
  setMessages: (messages: unknown[]) => Promise<void>;
  query: UseQueryResult<ChatCacheRecord | null>;
}

const GAME_SETTING_TYPES = new Set(['tone', 'setting', 'rules']);
const AGE_LEVEL_RANK: Record<string, number> = {
  age_0: 0,
  age_6: 6,
  age_12: 12,
  age_16: 16,
  age_18: 18,
};
const CHAT_CACHE_MAX_RECORDS = 100;
const CHAT_CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

interface WorldElementAgeFilter {
  ageLevel: string | null;
  blockExplicitWhenUnderAdultKnown: boolean;
}

export class SyncNotBootstrappedError extends Error {
  constructor() {
    super('sync_not_bootstrapped');
    this.name = 'SyncNotBootstrappedError';
  }
}

export class SyncBootstrapFetchError extends Error {
  readonly cause: unknown;

  constructor(cause: unknown) {
    super('sync_bootstrap_fetch_failed');
    this.name = 'SyncBootstrapFetchError';
    this.cause = cause;
  }
}

function nowIso(): string {
  return new Date().toISOString();
}

function numericId(id: number | string | null | undefined): number | null {
  if (id === null || id === undefined || id === '') return null;
  const value = Number(id);
  return Number.isFinite(value) ? value : null;
}

function sortByUpdatedAt<T extends { updated_at?: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => String(b.updated_at ?? '').localeCompare(String(a.updated_at ?? '')));
}

function favoriteRank(value: unknown): number {
  if (value === true || value === 1 || value === '1' || value === 'true') return 1;
  return 0;
}

function sortCharacters(rows: Character[]): Character[] {
  return [...rows].sort((a, b) => {
    const favoriteDelta = favoriteRank((b as { is_favorite?: unknown }).is_favorite)
      - favoriteRank((a as { is_favorite?: unknown }).is_favorite);
    if (favoriteDelta !== 0) return favoriteDelta;
    return String(b.updated_at ?? '').localeCompare(String(a.updated_at ?? ''));
  });
}

async function isBootstrapDone(db: LorathLocalDatabase): Promise<boolean> {
  return getMetaValue(db, 'bootstrapDone', false).catch(() => false);
}

// ─── Geteilter Vorlagenkatalog (W5-E, Plan 5.4) ─────────────────────────────
//
// Gelesen wird ab hier immer die VEREINIGUNG: eigene Zeilen (Nutzer-DB) ∪
// Katalogzeilen (kontouebergreifende Katalog-DB), begrenzt auf die
// Verweisliste `catalogVisible`. Diese Liste kommt vom Server und hat das
// Overlay (world_system_hidden), die Deduplizierung gegen eigene Kopien, die
// Kosmos-Zuordnung und die USK-Erzaehlersperre bereits angewandt — der Client
// rechnet nichts davon nach, er filtert nur nach dem, was drinsteht.
//
// Kosmos: Der Verweis traegt die kosmos_id DES NUTZERS (die geteilte Zeile
// selbst hat keine). Damit greift applyWorldElementFilter unveraendert fuer
// beide Quellen.

async function readCatalogRefs(db: LorathLocalDatabase): Promise<CatalogVisibleRecord[]> {
  try {
    return await db.catalogVisible.toArray();
  } catch {
    // Aeltere lokale DB ohne den Store (Dexie-Upgrade laeuft noch): dann gibt
    // es schlicht keine Katalogsicht — die eigenen Zeilen bleiben lesbar.
    return [];
  }
}

/** Eigene ∪ sichtbare Katalogelemente (Katalogzeile auf den Nutzer projiziert). */
export async function readVisibleWorldElements(db: LorathLocalDatabase): Promise<WorldElement[]> {
  const own = await db.worldElements.toArray();
  const refs = await readCatalogRefs(db);
  if (!refs.length) return own;
  const catalogRows = await readCatalogElements();
  if (!catalogRows.length) return own;

  const byId = new Map(catalogRows.map((row) => [Number(row.id), row]));
  const ownIds = new Set(own.map((row) => Number(row.id)));
  const merged = own.slice();
  for (const ref of refs) {
    const id = Number(ref.id);
    // Eine eigene Zeile gewinnt immer (Uebergangszeit: der Bestand kann eine
    // private Kopie UND den Verweis tragen).
    if (ownIds.has(id)) continue;
    const row = byId.get(id);
    if (!row) continue;
    merged.push({ ...row, kosmos_id: ref.kosmos_id ?? null });
  }
  return merged;
}

/** Eigene ∪ Katalogbeziehungen, deren beide Endpunkte sichtbar sind (Plan 2.7/9.9). */
export async function readVisibleWorldRelationships(db: LorathLocalDatabase): Promise<WorldRelationship[]> {
  const own = await db.worldRelationships.toArray();
  const refs = await readCatalogRefs(db);
  if (!refs.length) return own;
  const catalogRows = await readCatalogRelationships();
  if (!catalogRows.length) return own;

  const visibleIds = new Set(refs.map((ref) => Number(ref.id)));
  const ownIds = new Set(own.map((row) => Number(row.id)));
  const merged = own.slice();
  for (const row of catalogRows) {
    if (ownIds.has(Number(row.id))) continue;
    if (row.source_type === 'world_element' && !visibleIds.has(Number(row.source_id))) continue;
    if (row.target_type === 'world_element' && !visibleIds.has(Number(row.target_id))) continue;
    merged.push(row);
  }
  return merged;
}

/** Einzelnes Element: eigene Zeile, sonst sichtbare Katalogzeile. */
export async function readVisibleWorldElement(
  db: LorathLocalDatabase,
  id: number,
): Promise<WorldElement | null> {
  const own = await db.worldElements.get(id);
  if (own) return own;
  let ref: CatalogVisibleRecord | undefined;
  try {
    ref = await db.catalogVisible.get(id);
  } catch {
    ref = undefined;
  }
  if (!ref) return null;
  const row = await readCatalogElement(id);
  return row ? { ...row, kosmos_id: ref.kosmos_id ?? null } : null;
}

/** Elemente eines Weltenbaums aufloesen — Knoten duerfen auf Katalog-IDs zeigen. */
async function readVisibleWorldElementsByIds(
  db: LorathLocalDatabase,
  ids: number[],
): Promise<WorldElement[]> {
  if (!ids.length) return [];
  const own = (await db.worldElements.bulkGet(ids)).filter((row): row is WorldElement => !!row);
  const found = new Set(own.map((row) => Number(row.id)));
  const missing = ids.filter((id) => !found.has(Number(id)));
  if (!missing.length) return own;
  const refs = await readCatalogRefs(db);
  if (!refs.length) return own;
  const refById = new Map(refs.map((ref) => [Number(ref.id), ref]));
  const wanted = missing.filter((id) => refById.has(Number(id)));
  if (!wanted.length) return own;
  const catalogRows = await bulkGetCatalogElements(wanted);
  return own.concat(catalogRows.map((row) => ({
    ...row,
    kosmos_id: refById.get(Number(row.id))?.kosmos_id ?? null,
  })));
}

async function seedRows<T extends { id: number }>(
  table: Table<T, number>,
  rows: T[],
): Promise<void> {
  if (!rows.length) return;
  await table.bulkPut(rows);
}

function useLocalFirstList<T extends { id: number; updated_at?: string }>(
  queryKey: QueryKey,
  readLocal: (db: LorathLocalDatabase) => Promise<T[]>,
  fetchAndSeed?: (db: LorathLocalDatabase) => Promise<T[]>,
): UseQueryResult<T[]> {
  const db = useSyncDatabase();
  const online = useOnline();

  return useQuery({
    queryKey: [...queryKey, { syncOnline: online }],
    enabled: db !== null,
    queryFn: async () => {
      if (!db) return [];
      const bootstrapDone = await isBootstrapDone(db);
      if (!bootstrapDone && !online) throw new SyncNotBootstrappedError();
      if (fetchAndSeed && !bootstrapDone && online) {
        try {
          // P25-Verify-Härtung: eine unerwartete Server-Antwort ({} statt
          // Array — historisch real, vgl. sortCharactersForDisplay) darf NIE
          // die ganze Route in die Error-Boundary reißen. Nicht-Arrays fallen
          // auf den lokalen Bestand zurück.
          const fetched = await fetchAndSeed(db);
          if (Array.isArray(fetched)) return fetched;
          return readLocal(db);
        } catch (error) {
          const rows = await readLocal(db);
          if (!rows.length) throw new SyncBootstrapFetchError(error);
          return rows;
        }
      }
      return readLocal(db);
    },
  });
}

function useLocalFirstDetail<T extends { id: number }>(
  queryKey: QueryKey,
  id: number | string | null | undefined,
  readLocal: (db: LorathLocalDatabase, id: number) => Promise<T | null>,
  fetchAndSeed?: (db: LorathLocalDatabase, id: number) => Promise<T | null>,
): UseQueryResult<T | null> {
  const db = useSyncDatabase();
  const online = useOnline();
  const parsedId = numericId(id);

  return useQuery({
    queryKey: [...queryKey, { syncOnline: online }],
    enabled: db !== null && parsedId !== null,
    queryFn: async () => {
      if (!db || parsedId === null) return null;
      const bootstrapDone = await isBootstrapDone(db);
      if (!bootstrapDone && !online) throw new SyncNotBootstrappedError();
      if (fetchAndSeed && !bootstrapDone && online) {
        try {
          return await fetchAndSeed(db, parsedId);
        } catch (error) {
          const row = await readLocal(db, parsedId);
          if (!row) throw new SyncBootstrapFetchError(error);
          return row;
        }
      }
      return readLocal(db, parsedId);
    },
  });
}

// exportiert (named export) — Tests (frontend/src/sync/kosmos-filter.test.ts)
// prüfen das Verhalten von kosmosId, type und exclude_game_settings.
export function applyWorldElementFilter(rows: WorldElement[], filter?: WorldElementLocalFilter): WorldElement[] {
  let result = rows;
  if (filter?.type) result = result.filter((row) => row.type === filter.type);
  if (filter?.exclude_game_settings) result = result.filter((row) => !GAME_SETTING_TYPES.has(row.type));
  // Kosmos-Filter: eigener Kosmos + systemübergreifende Seeds (kosmos_id NULL).
  // Die Reihenfolge nach type/exclude_game_settings ist absichtlich — erst die
  // schmaleren Selektoren, dann der Kosmos-Schnitt. So bleibt der Code-Pfad
  // für Filter ohne kosmosId 100% rückwärtskompatibel.
  if (filter?.kosmosId != null) {
    const kosmosId = filter.kosmosId;
    result = result.filter((row) => row.kosmos_id === kosmosId || row.kosmos_id == null);
  }
  return result;
}

function extractAgeLevel(user: Record<string, unknown> | null): string | null {
  if (!user) return null;
  for (const key of ['selected_age_level', 'age_level', 'max_age_level']) {
    const value = user[key];
    if (typeof value === 'string' && value in AGE_LEVEL_RANK) return value;
  }
  return null;
}

function isKnownUnderAdult(user: Record<string, unknown> | null): boolean {
  if (!user) return false;
  const age = user.age;
  if (typeof age === 'number' && Number.isFinite(age)) return age < 18;
  if (user.age_verified === false || user.age_verified === 0) return true;
  return false;
}

function useWorldElementAgeFilter(): WorldElementAgeFilter {
  const { user } = useAuth();
  return useMemo(() => {
    const record = user as Record<string, unknown> | null;
    const ageLevel = extractAgeLevel(record);
    return {
      ageLevel,
      blockExplicitWhenUnderAdultKnown: !ageLevel && isKnownUnderAdult(record),
    };
  }, [user]);
}

function isAllowedForAge(requiredLevel: string, userAgeLevel: string): boolean {
  return (AGE_LEVEL_RANK[userAgeLevel] ?? -1) >= (AGE_LEVEL_RANK[requiredLevel] ?? Number.POSITIVE_INFINITY);
}

function isWorldElementAllowedByAge(row: WorldElement, ageFilter: WorldElementAgeFilter): boolean {
  if (row.type !== 'tone' || !isExplicitNsfwSystemNarrator(row)) return true;
  if (ageFilter.ageLevel) return isAllowedForAge('age_16', ageFilter.ageLevel);
  return !ageFilter.blockExplicitWhenUnderAdultKnown;
}

function applyWorldElementAgeFilter(rows: WorldElement[], ageFilter: WorldElementAgeFilter): WorldElement[] {
  return rows.filter((row) => isWorldElementAllowedByAge(row, ageFilter));
}

function applyWorldTreeAgeFilter(fullTree: WorldTreeFull | null, ageFilter: WorldElementAgeFilter): WorldTreeFull | null {
  if (!fullTree) return null;
  const blockedIds = new Set(
    fullTree.elements
      .filter((element) => !isWorldElementAllowedByAge(element, ageFilter))
      .map((element) => element.id),
  );
  if (!blockedIds.size) return fullTree;

  return {
    ...fullTree,
    elements: fullTree.elements.filter((element) => !blockedIds.has(element.id)),
    nodes: fullTree.nodes.filter((node) => !blockedIds.has(node.element_id)),
    relationships: fullTree.relationships.filter((relationship) => (
      !blockedIds.has(relationship.source_id) && !blockedIds.has(relationship.target_id)
    )),
  };
}

function worldElementQuery(filter?: WorldElementLocalFilter): Record<string, string | boolean> | undefined {
  if (!filter) return undefined;
  const query: Record<string, string | boolean> = {};
  if (filter.type) query.type = filter.type;
  if (filter.exclude_game_settings !== undefined) query.exclude_game_settings = filter.exclude_game_settings;
  // Warum: kosmosId ist REIN clientseitig. Der Server-Bootstrap zieht ohnehin
  // alle Kosmen (all_kosmos=true) und der Delta-Feed (sync-changes) ist nicht
  // gescoped — der Server darf den Filter also gar nicht sehen.
  return query;
}

function applyRelationshipFilter(
  rows: WorldRelationship[],
  filter?: WorldRelationshipLocalFilter,
): WorldRelationship[] {
  return rows.filter((row) => {
    if (filter?.sourceType && row.source_type !== filter.sourceType) return false;
    if (filter?.sourceId !== undefined && String(row.source_id) !== String(filter.sourceId)) return false;
    if (filter?.targetType && row.target_type !== filter.targetType) return false;
    if (filter?.targetId !== undefined && String(row.target_id) !== String(filter.targetId)) return false;
    return true;
  });
}

function toWorldTreeRecord(fullTree: WorldTreeFull): WorldTree {
  const tree: Partial<WorldTreeFull> = { ...fullTree };
  delete tree.nodes;
  delete tree.elements;
  delete tree.relationships;
  delete tree.lineSettings;
  return tree as WorldTree;
}

async function seedWorldTreeFull(db: LorathLocalDatabase, fullTree: WorldTreeFull): Promise<WorldTreeFull> {
  await db.transaction(
    'rw',
    db.worldTrees,
    db.worldTreeNodes,
    db.worldElements,
    db.worldRelationships,
    async () => {
      await db.worldTrees.put(toWorldTreeRecord(fullTree));
      await seedRows(db.worldTreeNodes, fullTree.nodes ?? []);
      await seedRows(db.worldElements, fullTree.elements ?? []);
      await seedRows(db.worldRelationships, fullTree.relationships ?? []);
    },
  );
  return fullTree;
}

async function readWorldTreeFullLocal(db: LorathLocalDatabase, id: number): Promise<WorldTreeFull | null> {
  const tree = await db.worldTrees.get(id);
  if (!tree) return null;

  const nodes = await db.worldTreeNodes.where('tree_id').equals(id).toArray();
  const elementIds = new Set(nodes.map((node) => node.element_id));
  // W5-E: Baumknoten duerfen auf Katalog-IDs zeigen (Plan 4.3) — ohne die
  // Vereinigung faellt jeder Katalogknoten aus dem Weltenbaum.
  const elements = await readVisibleWorldElementsByIds(db, [...elementIds]);
  const relationships = (await readVisibleWorldRelationships(db)).filter((relationship) => (
    relationship.source_type === 'world_element'
    && relationship.target_type === 'world_element'
    && elementIds.has(relationship.source_id)
    && elementIds.has(relationship.target_id)
  ));

  return { ...tree, nodes, elements, relationships, lineSettings: [] };
}

export function useCampaignsLocal(): UseQueryResult<Campaign[]> {
  return useLocalFirstList(
    queryKeys.campaigns.all,
    async (db) => sortByUpdatedAt(await db.campaigns.toArray()),
    async (db) => {
      const rows = await getCampaigns();
      await seedRows(db.campaigns, rows);
      return rows;
    },
  );
}

export function useCampaignLocal(id: number | string | null | undefined): UseQueryResult<Campaign | null> {
  return useLocalFirstDetail(
    queryKeys.campaigns.detail(id ?? ''),
    id,
    (db, campaignId) => db.campaigns.get(campaignId).then((row) => row ?? null),
    async (db, campaignId) => {
      const row = await getCampaign(campaignId);
      await db.campaigns.put(row);
      return row;
    },
  );
}

export function useCharactersLocal(): UseQueryResult<Character[]> {
  return useLocalFirstList(
    queryKeys.characters.all,
    async (db) => sortCharacters(await db.characters.toArray()),
    async (db) => {
      const rows = await getCharacters();
      await seedRows(db.characters, rows);
      return sortCharacters(rows);
    },
  );
}

export function useCharacterLocal(id: number | string | null | undefined): UseQueryResult<Character | null> {
  return useLocalFirstDetail(
    ['characters', String(id ?? '')],
    id,
    (db, characterId) => db.characters.get(characterId).then((row) => row ?? null),
    async (db, characterId) => {
      const row = await getCharacter(characterId);
      await db.characters.put(row);
      return row;
    },
  );
}

export function useWorldElementsLocal(filter?: WorldElementLocalFilter): UseQueryResult<WorldElement[]> {
  const ageFilter = useWorldElementAgeFilter();
  const apiQuery = worldElementQuery(filter);
  // Warum: kosmosId ist Teil des queryKey, damit react-query beim Switch
  // automatisch neu rendert. Da der Filter REIN clientseitig greift, wird der
  // QueryFn-Pfad lokal gefiltert — kein Server-Roundtrip beim Wechsel.
  const queryKey = queryKeys.worldElements.all({
    ...(apiQuery ?? {}),
    kosmosId: filter?.kosmosId ?? 'all',
    syncAgeLevel: ageFilter.ageLevel ?? (ageFilter.blockExplicitWhenUnderAdultKnown ? 'under-adult' : 'unknown'),
  });
  return useLocalFirstList(
    queryKey,
    async (db) => applyWorldElementFilter(
      applyWorldElementAgeFilter(sortByUpdatedAt(await readVisibleWorldElements(db)), ageFilter),
      filter,
    ),
    async (db) => {
      const rows = await getWorldElements(apiQuery);
      await seedRows(db.worldElements, rows);
      return applyWorldElementFilter(applyWorldElementAgeFilter(rows, ageFilter), filter);
    },
  );
}

export function useWorldElementLocal(id: number | string | null | undefined): UseQueryResult<WorldElement | null> {
  const ageFilter = useWorldElementAgeFilter();
  return useLocalFirstDetail(
    ['world-elements', String(id ?? ''), ageFilter.ageLevel ?? 'unknown'],
    id,
    (db, elementId) => readVisibleWorldElement(db, elementId)
      .then((row) => (row && isWorldElementAllowedByAge(row, ageFilter) ? row : null)),
    async (db, elementId) => {
      const row = await getWorldElement(elementId);
      // Audit 05-P2-7 (Defense-in-depth): Age-Check VOR dem Schreiben in Dexie.
      // Vorher landeten altersgesperrte (nsfw) Zeilen im Offline-Cache, und
      // sobald der useLocalFirstDetail-Reader einen Treffer hatte, blubberte
      // der verbotene Inhalt durch die UI. Server-Gate bleibt die erste
      // Verteidigungslinie, dies ist die zweite.
      if (!isWorldElementAllowedByAge(row, ageFilter)) return null;
      await db.worldElements.put(row);
      return row;
    },
  );
}

export function useWorldRelationshipsLocal(
  filter?: WorldRelationshipLocalFilter,
): UseQueryResult<WorldRelationship[]> {
  return useLocalFirstList(
    ['world-relationships', filter ?? {}],
    async (db) => applyRelationshipFilter(await readVisibleWorldRelationships(db), filter),
    async (db) => {
      let rows: WorldRelationship[] | null = null;
      if (filter?.sourceType && filter.sourceId !== undefined) {
        rows = await getWorldRelationshipsForSource(filter.sourceType, filter.sourceId);
      } else if (filter?.targetType && filter.targetId !== undefined) {
        rows = await getWorldRelationshipsToTarget(filter.targetType, filter.targetId);
      }
      if (!rows) return applyRelationshipFilter(await readVisibleWorldRelationships(db), filter);
      await seedRows(db.worldRelationships, rows);
      return applyRelationshipFilter(rows, filter);
    },
  );
}

export function useWorldRelationshipLocal(id: number | string | null | undefined): UseQueryResult<WorldRelationship | null> {
  return useLocalFirstDetail(
    ['world-relationships', String(id ?? '')],
    id,
    async (db, relationshipId) => {
      const own = await db.worldRelationships.get(relationshipId);
      if (own) return own;
      const rows = await readVisibleWorldRelationships(db);
      return rows.find((row) => Number(row.id) === relationshipId) ?? null;
    },
  );
}

export function useWorldTreesLocal(): UseQueryResult<WorldTree[]> {
  return useLocalFirstList(
    ['world-trees'],
    async (db) => sortByUpdatedAt(await db.worldTrees.toArray()),
    async (db) => {
      const summaries = await getWorldTrees();
      const fullTrees = await Promise.all(summaries.map((tree) => getWorldTree(tree.id)));
      await Promise.all(fullTrees.map((tree) => seedWorldTreeFull(db, tree)));
      return fullTrees.map(toWorldTreeRecord);
    },
  );
}

export function useWorldTreeLocal(id: number | string | null | undefined): UseQueryResult<WorldTreeFull | null> {
  const ageFilter = useWorldElementAgeFilter();
  return useLocalFirstDetail(
    ['world-trees', String(id ?? ''), ageFilter.ageLevel ?? 'unknown'],
    id,
    async (db, treeId) => applyWorldTreeAgeFilter(await readWorldTreeFullLocal(db, treeId), ageFilter),
    async (db, treeId) => applyWorldTreeAgeFilter(await seedWorldTreeFull(db, await getWorldTree(treeId)), ageFilter),
  );
}

export function useWorldTreeNodesLocal(treeId?: number | string | null): UseQueryResult<WorldTreeNode[]> {
  return useLocalFirstList(
    ['world-tree-nodes', treeId === undefined || treeId === null ? 'all' : String(treeId)],
    async (db) => {
      const parsedTreeId = numericId(treeId);
      const rows = parsedTreeId === null
        ? await db.worldTreeNodes.toArray()
        : await db.worldTreeNodes.where('tree_id').equals(parsedTreeId).toArray();
      return sortByUpdatedAt(rows);
    },
    async (db) => {
      const parsedTreeId = numericId(treeId);
      if (parsedTreeId === null) return sortByUpdatedAt(await db.worldTreeNodes.toArray());
      const fullTree = await seedWorldTreeFull(db, await getWorldTree(parsedTreeId));
      return sortByUpdatedAt(fullTree.nodes ?? []);
    },
  );
}

export function useTableRoundsLocal(): UseQueryResult<TableRound[]> {
  return useLocalFirstList(
    queryKeys.tableRounds.all,
    async (db) => sortByUpdatedAt(await db.tableRounds.toArray()),
    async (db) => {
      const rows = await getTableRounds();
      await seedRows(db.tableRounds, rows);
      return rows;
    },
  );
}

export function useTableRoundLocal(id: number | string | null | undefined): UseQueryResult<TableRound | null> {
  return useLocalFirstDetail(
    queryKeys.tableRounds.detail(id ?? ''),
    id,
    (db, roundId) => db.tableRounds.get(roundId).then((row) => row ?? null),
    async (db, roundId) => {
      const row = await getTableRound(roundId);
      await db.tableRounds.put(row);
      return row;
    },
  );
}

export function useChatCache(kind: ChatCacheKind, id: number | string | null | undefined): ChatCacheHookResult {
  const db = useSyncDatabase();
  const queryClient = useQueryClient();
  const key = id === null || id === undefined ? '' : chatCacheKey(kind, id);
  const queryKey = useMemo(() => ['chat-cache', kind, String(id ?? '')] as const, [id, kind]);

  const query = useQuery({
    queryKey,
    enabled: db !== null && key !== '',
    queryFn: async () => {
      if (!db || !key) return null;
      return await db.chatCache.get(key) ?? null;
    },
  });

  const setMessages = useCallback(async (messages: unknown[]) => {
    if (!db || !key || id === null || id === undefined) return;
    const record: ChatCacheRecord = {
      key,
      kind,
      id,
      messages,
      fetchedAt: nowIso(),
    };
    await db.transaction('rw', db.chatCache, async () => {
      await db.chatCache.put(record);
      const cutoff = new Date(Date.now() - CHAT_CACHE_MAX_AGE_MS).toISOString();
      await db.chatCache.where('fetchedAt').below(cutoff).delete();
      const count = await db.chatCache.count();
      if (count > CHAT_CACHE_MAX_RECORDS) {
        const staleKeys = await db.chatCache
          .orderBy('fetchedAt')
          .limit(count - CHAT_CACHE_MAX_RECORDS)
          .primaryKeys();
        await db.chatCache.bulkDelete(staleKeys);
      }
    });
    queryClient.setQueryData(queryKey, record);
  }, [db, id, key, kind, queryClient, queryKey]);

  return {
    messages: query.data?.messages ?? [],
    fetchedAt: query.data?.fetchedAt ?? null,
    setMessages,
    query,
  };
}
